/*
 * Copyright (c) 2024. World Wide Generation Ltd
 */
import { g17ecoApi, transformResponse } from '@api/g17ecoApi';
import { ExternalEntity, IntegrationConfigurationOption, IntegrationConnectionInfo, IntegrationProvider, IntegrationProviderExtended, ProviderSetupInfo, SetupData, SubConnection } from '@g17eco/types/app-integration';
import type { DataTransferResponse, DataTransferPlain, DataTransferDashboardResponse, DataTransferJob, DataTransferUtrsResponse, RowApprovalStatus } from '@g17eco/types/data-transfer';
import { DataPeriods } from '@utils/dataPeriods';
import { ValueData } from '../types/universalTrackerValue';


interface PartnerGet {
  initiativeId: string,
  code: string
}

interface InitiativeAppIntegrationsResponse {
  allProviders: IntegrationProvider[];
  usedProviders: IntegrationProviderExtended[];
}

interface SubConnectionCreate {
  initiativeId: string;
  code: string;
  targetInitiativeId: string;
  externalId: string;
  facilityName: string;
}

interface SubConnectionUpdate {
  initiativeId: string;
  code: string;
  taskId: string;
  targetInitiativeId?: string;
  externalId?: string;
  facilityName?: string;
}

interface SubConnectionDelete {
  initiativeId: string;
  code: string;
  taskId: string;
}

interface ConnectionDataUpdate {
  initiativeId: string;
  code: string;
  externalId: string;
}

interface DataTransferQuery {
  initiativeId: string;
  code: string;
  childInitiativeId: string;
  startDate: string;
  endDate: string;
}

export const integrationsApi = g17ecoApi
  .enhanceEndpoints({
    addTagTypes: ['integrations', 'integration', 'integration-setup-info', 'sub-connections', 'external-entities', 'integration-configuration-options', 'data-transfers', 'data-transfer', 'data-transfer-job'],
  })
  .injectEndpoints({
    endpoints: (builder) => ({
      listInitiativeAppIntegrations: builder.query<InitiativeAppIntegrationsResponse, string>({
        transformResponse,
        query: (initiativeId) => ({
          url: `/initiatives/${initiativeId}/integrations`,
          method: 'get',
        }),
        providesTags: ['integrations']
      }),
      getInitiativeAppIntegration: builder.query<ProviderSetupInfo, PartnerGet>({
        transformResponse,
        query: ({ initiativeId, code }) => ({
          url: `/initiatives/${initiativeId}/integrations/${code}`,
          method: 'get',
        }),
        providesTags: (_result, _error, arg) => [{ type: 'integration-setup-info', id: arg.code }],
      }),
      getUsedInitiativeAppIntegration: builder.query<
        IntegrationConnectionInfo<{ answers: { valueData: ValueData }[] }>,
        PartnerGet
      >({
        transformResponse,
        query: ({ initiativeId, code }) => ({
          url: `/initiatives/${initiativeId}/integrations/${code}/used`,
          method: 'get',
        }),
        providesTags: (_result, _error, arg) => [{ type: 'integration', id: arg.code }],
      }),
      createInitiativeAppIntegration: builder.mutation<ProviderSetupInfo, SetupData>({
        transformResponse,
        query: ({ initiativeId, code, ...data }) => ({
          url: `/initiatives/${initiativeId}/integrations/${code}`,
          method: 'post',
          data: data,
        }),
        invalidatesTags: (_result, _error, agr) => ['integrations', { type: 'integration-setup-info', id: agr.code }],
      }),
      listExternalEntities: builder.query<ExternalEntity[], PartnerGet>({
        transformResponse,
        query: ({ initiativeId, code }) => ({
          url: `/initiatives/${initiativeId}/integrations/${code}/external-entities`,
          method: 'get',
        }),
        providesTags: (_result, _error, arg) => [{ type: 'external-entities', id: arg.code }],
      }),
      listConfigurationOptions: builder.query<IntegrationConfigurationOption[], PartnerGet>({
        transformResponse,
        query: ({ initiativeId, code }) => ({
          url: `/initiatives/${initiativeId}/integrations/${code}/configuration-options`,
          method: 'get',
        }),
        providesTags: (_result, _error, arg) => [{ type: 'integration-configuration-options', id: arg.code }],
      }),
      applyConfigurationOption: builder.mutation<{ success: boolean }, PartnerGet & { optionId: string }>({
        transformResponse,
        query: ({ initiativeId, code, optionId }) => ({
          url: `/initiatives/${initiativeId}/integrations/${code}/configuration`,
          method: 'put',
          data: { optionId },
        }),
        invalidatesTags: (_result, _error, arg) => [
          { type: 'integration', id: arg.code },
          { type: 'external-entities', id: arg.code },
          { type: 'integration-configuration-options', id: arg.code },
        ],
      }),
      listSubConnections: builder.query<SubConnection[], PartnerGet>({
        transformResponse,
        query: ({ initiativeId, code }) => ({
          url: `/initiatives/${initiativeId}/integrations/${code}/sub-connections`,
          method: 'get',
        }),
        providesTags: (_result, _error, arg) => [{ type: 'sub-connections', id: arg.code }],
      }),
      createSubConnection: builder.mutation<SubConnection, SubConnectionCreate>({
        transformResponse,
        query: ({ initiativeId, code, targetInitiativeId, externalId, facilityName }) => ({
          url: `/initiatives/${initiativeId}/integrations/${code}/sub-connections`,
          method: 'post',
          data: { initiativeId: targetInitiativeId, externalId, facilityName },
        }),
        invalidatesTags: (_result, _error, arg) => [{ type: 'sub-connections', id: arg.code }],
      }),
      updateSubConnection: builder.mutation<SubConnection, SubConnectionUpdate>({
        transformResponse,
        query: ({ initiativeId, code, taskId, targetInitiativeId, externalId, facilityName }) => ({
          url: `/initiatives/${initiativeId}/integrations/${code}/sub-connections/${taskId}`,
          method: 'patch',
          data: {
            ...(targetInitiativeId ? { initiativeId: targetInitiativeId } : {}),
            ...(externalId ? { externalId } : {}),
            ...(facilityName ? { facilityName } : {}),
          },
        }),
        invalidatesTags: (_result, _error, arg) => [{ type: 'sub-connections', id: arg.code }],
      }),
      deleteSubConnection: builder.mutation<{ success: boolean }, SubConnectionDelete>({
        transformResponse,
        query: ({ initiativeId, code, taskId }) => ({
          url: `/initiatives/${initiativeId}/integrations/${code}/sub-connections/${taskId}`,
          method: 'delete',
        }),
        invalidatesTags: (_result, _error, arg) => [{ type: 'sub-connections', id: arg.code }],
      }),
      updateConnectionExternalId: builder.mutation<unknown, ConnectionDataUpdate>({
        transformResponse,
        query: ({ initiativeId, code, externalId }) => ({
          url: `/initiatives/${initiativeId}/integrations/${code}/connection-data`,
          method: 'patch',
          data: { externalId },
        }),
        invalidatesTags: (_result, _error, arg) => [{ type: 'integration', id: arg.code }],
      }),
      getDataTransferComparison: builder.query<DataTransferResponse, DataTransferQuery>({
        transformResponse,
        query: ({ initiativeId, code, childInitiativeId, startDate, endDate }) => ({
          url: `/initiatives/${initiativeId}/integrations/${code}/data-transfer`,
          method: 'get',
          params: { childInitiativeId, startDate, endDate },
        }),
      }),
      getDataTransferUtrs: builder.query<DataTransferUtrsResponse, PartnerGet>({
        transformResponse,
        query: ({ initiativeId, code }) => ({
          url: `/initiatives/${initiativeId}/integrations/${code}/data-transfers/utrs`,
          method: 'get',
        }),
      }),
      // --- Data Transfer Sync endpoints ---
      createDataTransfer: builder.mutation<DataTransferPlain, {
        initiativeId: string;
        code: string;
        name: string;
        startDate: string;
        endDate: string;
        frequency: DataPeriods;
        childInitiativeIds: string[];
      }>({
        transformResponse,
        query: ({ initiativeId, code, ...data }) => ({
          url: `/initiatives/${initiativeId}/integrations/${code}/data-transfers`,
          method: 'post',
          data,
        }),
        invalidatesTags: ['data-transfers'],
      }),
      listDataTransfers: builder.query<DataTransferPlain[], PartnerGet>({
        transformResponse,
        query: ({ initiativeId, code }) => ({
          url: `/initiatives/${initiativeId}/integrations/${code}/data-transfers`,
          method: 'get',
        }),
        providesTags: ['data-transfers'],
      }),
      getDataTransferDashboard: builder.query<DataTransferDashboardResponse, PartnerGet & { dataTransferId: string }>({
        transformResponse,
        query: ({ initiativeId, code, dataTransferId }) => ({
          url: `/initiatives/${initiativeId}/integrations/${code}/data-transfers/${dataTransferId}`,
          method: 'get',
        }),
        providesTags: (_result, _error, arg) => [{ type: 'data-transfer', id: arg.dataTransferId }],
      }),
      getDataTransferSubsidiaryJobs: builder.query<DataTransferJob[], PartnerGet & { dataTransferId: string; subsidiaryId: string }>({
        transformResponse,
        query: ({ initiativeId, code, dataTransferId, subsidiaryId }) => ({
          url: `/initiatives/${initiativeId}/integrations/${code}/data-transfers/${dataTransferId}/subsidiaries/${subsidiaryId}/jobs`,
          method: 'get',
        }),
        providesTags: (_result, _error, arg) => [{ type: 'data-transfer', id: `${arg.dataTransferId}-${arg.subsidiaryId}` }],
      }),
      getDataTransferJob: builder.query<DataTransferJob, PartnerGet & { dataTransferId: string; jobId: string }>({
        transformResponse,
        query: ({ initiativeId, code, dataTransferId, jobId }) => ({
          url: `/initiatives/${initiativeId}/integrations/${code}/data-transfers/${dataTransferId}/jobs/${jobId}`,
          method: 'get',
        }),
        providesTags: (_result, _error, arg) => [{ type: 'data-transfer-job', id: arg.jobId }],
      }),
      submitDataTransferReview: builder.mutation<{ success: boolean }, PartnerGet & {
        dataTransferId: string;
        jobId: string;
        taskId: string;
        approval?: RowApprovalStatus;
        entries?: Array<{ key: string; approval: RowApprovalStatus }>;
      }>({
        transformResponse,
        query: ({ initiativeId, code, dataTransferId, jobId, ...data }) => ({
          url: `/initiatives/${initiativeId}/integrations/${code}/data-transfers/${dataTransferId}/jobs/${jobId}/review`,
          method: 'post',
          data,
        }),
        invalidatesTags: (_result, _error, arg) => [
          { type: 'data-transfer-job', id: arg.jobId },
          'data-transfer',
        ],
      }),
      triggerDataTransferSync: builder.mutation<{ success: boolean }, PartnerGet & { dataTransferId: string; jobId: string }>({
        transformResponse,
        query: ({ initiativeId, code, dataTransferId, jobId }) => ({
          url: `/initiatives/${initiativeId}/integrations/${code}/data-transfers/${dataTransferId}/jobs/${jobId}/sync`,
          method: 'post',
        }),
        invalidatesTags: (_result, _error, arg) => [
          { type: 'data-transfer-job', id: arg.jobId },
          'data-transfer',
        ],
      }),
      triggerDataTransferSyncTask: builder.mutation<{ success: boolean }, PartnerGet & { dataTransferId: string; jobId: string; taskId: string }>({
        transformResponse,
        query: ({ initiativeId, code, dataTransferId, jobId, taskId }) => ({
          url: `/initiatives/${initiativeId}/integrations/${code}/data-transfers/${dataTransferId}/jobs/${jobId}/sync/${taskId}`,
          method: 'post',
        }),
        invalidatesTags: (_result, _error, arg) => [
          { type: 'data-transfer-job', id: arg.jobId },
          'data-transfer',
        ],
      }),
      regenerateDataTransferPending: builder.mutation<{ preserved: number; removed: number }, PartnerGet & { dataTransferId: string; jobId: string }>({
        transformResponse,
        query: ({ initiativeId, code, dataTransferId, jobId }) => ({
          url: `/initiatives/${initiativeId}/integrations/${code}/data-transfers/${dataTransferId}/jobs/${jobId}/regenerate-pending`,
          method: 'post',
        }),
        invalidatesTags: (_result, _error, arg) => [
          { type: 'data-transfer-job', id: arg.jobId },
          'data-transfer',
        ],
      }),
      completeDataTransferJob: builder.mutation<{ success: boolean }, PartnerGet & { dataTransferId: string; jobId: string }>({
        transformResponse,
        query: ({ initiativeId, code, dataTransferId, jobId }) => ({
          url: `/initiatives/${initiativeId}/integrations/${code}/data-transfers/${dataTransferId}/jobs/${jobId}/complete`,
          method: 'post',
        }),
        invalidatesTags: (_result, _error, arg) => [
          { type: 'data-transfer-job', id: arg.jobId },
          'data-transfer',
        ],
      }),
      reopenDataTransferJob: builder.mutation<{ success: boolean }, PartnerGet & { dataTransferId: string; jobId: string }>({
        transformResponse,
        query: ({ initiativeId, code, dataTransferId, jobId }) => ({
          url: `/initiatives/${initiativeId}/integrations/${code}/data-transfers/${dataTransferId}/jobs/${jobId}/complete`,
          method: 'delete',
        }),
        invalidatesTags: (_result, _error, arg) => [
          { type: 'data-transfer-job', id: arg.jobId },
          'data-transfer',
        ],
      }),
      retryDataTransferSync: builder.mutation<{ success: boolean }, PartnerGet & { dataTransferId: string; jobId: string }>({
        transformResponse,
        query: ({ initiativeId, code, dataTransferId, jobId }) => ({
          url: `/initiatives/${initiativeId}/integrations/${code}/data-transfers/${dataTransferId}/jobs/${jobId}/retry-sync`,
          method: 'post',
        }),
        invalidatesTags: (_result, _error, arg) => [
          { type: 'data-transfer-job', id: arg.jobId },
          'data-transfer',
        ],
      }),
      getDataTransferExport: builder.query<Blob, DataTransferQuery>({
        query: ({ initiativeId, code, childInitiativeId, startDate, endDate }) => ({
          url: `/initiatives/${initiativeId}/integrations/${code}/data-transfer/export`,
          method: 'get',
          params: { childInitiativeId, startDate, endDate },
          responseType: 'blob',
        }),
      }),
    }),
  });

export const {
  useGetInitiativeAppIntegrationQuery,
  useListInitiativeAppIntegrationsQuery,
  useGetUsedInitiativeAppIntegrationQuery,
  useCreateInitiativeAppIntegrationMutation,
  useListExternalEntitiesQuery,
  useListConfigurationOptionsQuery,
  useApplyConfigurationOptionMutation,
  useListSubConnectionsQuery,
  useCreateSubConnectionMutation,
  useUpdateSubConnectionMutation,
  useDeleteSubConnectionMutation,
  useUpdateConnectionExternalIdMutation,
  useGetDataTransferComparisonQuery,
  useGetDataTransferUtrsQuery,
  useLazyGetDataTransferExportQuery,
  useCreateDataTransferMutation,
  useListDataTransfersQuery,
  useGetDataTransferDashboardQuery,
  useGetDataTransferJobQuery,
  useSubmitDataTransferReviewMutation,
  useTriggerDataTransferSyncMutation,
  useTriggerDataTransferSyncTaskMutation,
  useRegenerateDataTransferPendingMutation,
  useCompleteDataTransferJobMutation,
  useReopenDataTransferJobMutation,
  useRetryDataTransferSyncMutation,
  useGetDataTransferSubsidiaryJobsQuery,
} = integrationsApi;
